import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check } from "lucide-react";

const DEFAULT_ACCENTS = { rookie: "#A1A1AA", pro: "var(--cl-orange)", hof: "var(--cl-gold)" };

export default function Subscribe() {
  const [busy, setBusy] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [settings, setSettings] = useState({});
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/config")
      .then((r) => setTiers(r.data.tiers || []))
      .catch((err) => console.warn("Failed to load tiers:", err?.message));
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const subscribe = async (tier) => {
    if (!user) { toast.error("Please log in to subscribe"); nav("/login?next=/subscribe"); return; }
    setBusy(tier);
    try {
      const r = await api.post("/payments/subscribe", { tier, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start checkout");
      setBusy(null);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
      <div className="max-w-3xl">
        <div className="text-label text-[var(--cl-primary)]">{settings.subscribe_kicker || "The Card Lobster Club"}</div>
        <h1 className="font-display text-5xl sm:text-7xl mt-2">{settings.subscribe_title || "Choose Your Tier"}</h1>
        <p className="text-zinc-300 text-lg mt-4">{settings.subscribe_subtitle || "Monthly membership perks that stack. Cancel anytime. Discounts apply automatically at checkout for logged-in members."}</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mt-14">
        {tiers.map((t) => {
          const accent = t.accent || DEFAULT_ACCENTS[t.key] || "#FFFFFF";
          const isCurrent = user?.subscription_tier === t.key;
          return (
            <div
              key={t.key}
              data-testid={`tier-card-${t.key}`}
              className={`relative rounded-md p-8 flex flex-col ${t.highlight ? "bg-gradient-to-b from-[#1a0505] to-[var(--cl-surface)] border border-[var(--cl-orange)]/50" : "bg-[var(--cl-surface)] border border-white/10"}`}
            >
              {t.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--cl-orange)] text-black text-[10px] font-bold tracking-widest px-3 py-1 rounded">
                  MOST POPULAR
                </span>
              )}
              <div className="text-label" style={{ color: accent }}>{t.tagline}</div>
              <h3 className="font-display text-4xl mt-2">{t.name}</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-5xl">${t.price}</span>
                <span className="text-zinc-500 text-sm">/mo</span>
              </div>
              <ul className="mt-6 space-y-3 flex-1">
                {(t.perks || []).map((p) => (
                  <li key={p} className="flex gap-3 text-sm text-zinc-200">
                    <Check size={16} style={{ color: accent, flexShrink: 0, marginTop: 2 }} />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid={`subscribe-btn-${t.key}`}
                disabled={!!busy || isCurrent}
                onClick={() => subscribe(t.key)}
                className={`mt-8 ${t.highlight ? "btn-primary" : "btn-outline"} w-full`}
              >
                {isCurrent ? "Current Tier" : busy === t.key ? "Redirecting…" : "Subscribe Now"}
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-16 border border-white/10 rounded-md p-8 bg-[var(--cl-surface)]">
        <div className="text-label text-zinc-500 mb-4">Perks Comparison</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="text-left text-zinc-500">
                <th className="py-3 font-normal text-label">Perk</th>
                {tiers.map((t) => <th key={t.key} className="py-3 font-display text-xl">{t.name}</th>)}
              </tr>
            </thead>
            <tbody className="border-t border-white/10">
              <PerkRow label="Storewide Discount" tiers={tiers} render={(t) => `${t.discount_pct}%`} />
              <PerkRow label="Monthly Perks" tiers={tiers} render={(t) => (t.perks || [])[0] || "—"} />
              <PerkRow label="Access Level" tiers={tiers} render={(t) => (t.perks || [])[2] || "—"} />
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PerkRow({ label, tiers, render }) {
  return (
    <tr className="border-t border-white/5">
      <td className="py-3 text-zinc-400">{label}</td>
      {tiers.map((t) => <td key={t.key} className="py-3 text-white">{render(t)}</td>)}
    </tr>
  );
}
